import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const CookieBanner = ({ isIntroFinished }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if preferences already exist
    const savedPrefs = localStorage.getItem("nw_cookie_prefs");
    
    // Only show if no preferences saved AND intro is finished
    if (!savedPrefs && isIntroFinished) {
      // Small delay for cinematic entrance
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isIntroFinished]);

  const handleAcceptAll = () => {
    const allOn = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem("nw_cookie_prefs", JSON.stringify(allOn));
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0, scale: 0.9 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 50, opacity: 0, scale: 0.95 }}
          transition={{ 
            type: "spring", 
            stiffness: 260, 
            damping: 25,
            delay: 0.2
          }}
          className="fixed bottom-8 left-8 right-8 md:left-auto md:max-w-md z-[200]"
        >
          <div className="relative overflow-hidden rounded-3xl bg-[#1e1e21]/80 backdrop-blur-2xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-6 md:p-8">
            {/* Animated background glow */}
            <motion.div 
              animate={{ 
                opacity: [0.1, 0.2, 0.1],
                scale: [1, 1.1, 1]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-24 -right-24 w-48 h-48 bg-[#5742bb] rounded-full blur-[60px] pointer-events-none"
            />

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex-shrink-0">
                  <svg
                    viewBox="0 0 64 64"
                    className="w-full h-full drop-shadow-lg"
                    fill="none"
                  >
                    <path
                      d="M32 6
                         C28.8 6 26.5 8.7 26.9 11.8
                         C27.2 14.5 25.3 17 22.7 17.4
                         C19.7 17.8 17.5 20.5 18 23.5
                         C18.4 26.1 16.6 28.6 14 29
                         C10.9 29.5 8.5 32.2 8.5 35.4
                         C8.5 45.7 16.8 54 27.1 54
                         H32
                         C42.5 54 51 45.5 51 35
                         C51 33.8 50.9 32.7 50.7 31.6
                         C47.5 31.2 45 28.5 45 25.2
                         C45 22 47.4 19.3 50.5 18.8
                         C49.1 11.5 42.7 6 35 6
                         H32Z"
                      fill="#D89B4D"
                    />
                    <circle cx="22" cy="24" r="4.2" fill="#6B4328" />
                    <circle cx="34" cy="20" r="3.2" fill="#6B4328" />
                    <circle cx="37" cy="33" r="4" fill="#6B4328" />
                    <circle cx="24" cy="38" r="3.4" fill="#6B4328" />
                    <circle cx="33" cy="45" r="2.8" fill="#6B4328" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold tracking-tight text-white">Intelligence Optimization</h3>
              </div>

              <p className="text-sm text-white/70 leading-relaxed mb-6">
                We use cookies to refine our neural architectures and enhance your experience. By continuing, you agree to our data sovereignty protocols.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <motion.button
                  whileHover={{ scale: 1.02, backgroundColor: "#4835a0" }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAcceptAll}
                  className="flex-1 px-6 py-3 rounded-xl bg-[#5742bb] text-white font-bold text-sm shadow-lg shadow-[#5742bb]/20 transition-colors"
                >
                  Accept All
                </motion.button>
                <Link 
                  to="/cookie-policy" 
                  className="flex-1 px-6 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white font-bold text-sm text-center transition-colors"
                  onClick={() => setIsVisible(false)}
                >
                  Manage
                </Link>
              </div>

              <div className="mt-4 text-center">
                <Link to="/privacy" className="text-[10px] uppercase tracking-widest text-white/30 hover:text-white/60 transition-colors font-bold">
                  Privacy Framework Overview
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
